import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";

/**
 * The PayPack lens: a list of people on the left, one statement on the right.
 *
 * Two shapes, so two skeletons would be two half-truths. `body` draws only the
 * statement side, for the wait that happens when a reader picks a different
 * technician and the list beside it is already on screen.
 */
const ROWS = ["w-32", "w-40", "w-28", "w-36", "w-44", "w-24", "w-40", "w-32"];

const Body = () => (
  <Stack
    gap="none"
    align="stretch"
    direction="column"
    aria-hidden
    className="flex min-h-0 flex-1 flex-col gap-4 p-4"
  >
    <Skeleton className="h-4 w-48" />
    <Stack
      gap="none"
      align="stretch"
      direction="column"
      className="flex flex-col gap-2"
    >
      {["w-full", "w-11/12", "w-4/5"].map((width) => (
        <Skeleton key={width} className={cn("h-3", width)} />
      ))}
    </Stack>
    {/* The line-item table each statement carries. */}
    <Stack
      gap="none"
      align="stretch"
      direction="column"
      className="flex flex-col"
    >
      {Array.from({ length: 10 }, (_, row) => (
        <Stack
          gap="none"
          align="stretch"
          direction="row"
          key={row}
          className="flex items-center gap-3 border-b border-stroke-tertiary py-2"
        >
          <Skeleton className="h-3 w-56" />
          <Skeleton className="ml-auto h-3 w-16" />
          <Skeleton className="h-3 w-16" />
        </Stack>
      ))}
    </Stack>
  </Stack>
);

export const StatementsSkeleton = ({ body = false }: { body?: boolean }) => {
  if (body) return <Body />;
  return (
    <Stack
      gap="none"
      align="stretch"
      direction="row"
      role="status"
      aria-label="Loading PayPacks…"
      className="flex min-h-0 flex-1"
    >
      <Stack
        gap="none"
        align="stretch"
        direction="column"
        aria-hidden
        className="flex w-64 shrink-0 flex-col border-r border-stroke-tertiary"
      >
        {Array.from({ length: 24 }, (_, row) => (
          <Stack
            gap="none"
            align="stretch"
            direction="row"
            key={row}
            className="flex items-center gap-2 border-b border-stroke-tertiary px-3 py-2"
          >
            <Skeleton className={cn("h-3", ROWS[row % ROWS.length])} />
          </Stack>
        ))}
      </Stack>
      <Body />
    </Stack>
  );
};
